import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Skeleton } from '@mui/material';
import { useGetProfileTradeHistoryQuery } from 'store/services/profile/profileApi';
import { ProfileTrade, ProfileTradeHistoryItem } from 'types/interfaces/profile';
import { Points } from 'components/Points/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { buildCardUrl } from 'utils/cardUrlUtils';
import './TradeHistoryTab.css';
import { useIsMobile } from 'hooks/useIsMobile';
import { useProfileTradeSidebar } from '../../context/ProfileTradeSidebarContext';
import { useCardFilterState } from 'components/FilterPanel/cardFilterUrlState';
import CardFilterRail from 'components/FilterPanel/CardFilterRail';
import CardFilterTokenRow from 'components/FilterPanel/CardFilterTokenRow';
import CardFilterMobile from 'components/FilterPanel/CardFilterMobile';
import FilteredEmptyState from 'components/FilterPanel/FilteredEmptyState';

const PROFILE_FILTER_TOOLTIP = {
  heading: "Filter this trader's list",
  body: "Narrows every card shown below. Cards already in your trade are unaffected. Color filters match a card's printed colors; the Color sort groups by color identity.",
};

interface TradeHistoryTabProps {
  username: string;
}

// Format date as relative or absolute
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

// Format number with commas
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

// Card item component
const TradeCardItem: React.FC<{ item: ProfileTradeHistoryItem; type: 'sent' | 'received' }> = ({
  item,
  type,
}) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;

  const handleClick = () => {
    if (item.scryfallId) {
      navigate(buildCardUrl({ name: item.cardName, scryfallId: item.scryfallId }));
    }
  };

  return (
    <div className="trade-history-card-item" onClick={handleClick}>
      <div className="trade-history-card-image-container">
        <img
          src={imageError || !item.imageUrl ? defaultImage : item.imageUrl}
          alt={item.cardName}
          className="trade-history-card-image"
          onError={() => setImageError(true)}
          loading="lazy"
        />
        <div className={`trade-history-card-badge ${type}`}>
          {type === 'sent' ? 'Sent' : 'Received'}
        </div>
      </div>
      <div className="trade-history-card-points">
        <Points points={item.pointValue} type="small" />
      </div>
    </div>
  );
};

// Trade group component
const TradeGroup: React.FC<{ trade: ProfileTrade }> = ({ trade }) => {
  return (
    <div className="trade-history-group">
      <div className="trade-history-group-header">
        <span className={`trade-history-group-type ${trade.type}`}>
          {trade.type === 'sent' ? 'Sent' : 'Received'}
        </span>
        <span className="trade-history-group-date">{formatDate(trade.completedAt)}</span>
      </div>
      <div className="trade-history-cards-grid">
        {trade.items.map((item, index) => (
          <TradeCardItem key={`${trade.id}-${index}`} item={item} type={trade.type} />
        ))}
      </div>
    </div>
  );
};

// Loading skeleton
const TradeHistorySkeleton: React.FC = () => (
  <div className="trade-history-skeleton">
    {[...Array(3)].map((_, groupIndex) => (
      <div key={groupIndex} className="trade-history-group">
        <div className="trade-history-group-header">
          <Skeleton variant="text" width={80} height={20} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
          <Skeleton variant="text" width={60} height={20} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
        </div>
        <div className="trade-history-cards-grid">
          {[...Array(4)].map((_, cardIndex) => (
            <div key={cardIndex} className="trade-history-card-item">
              <Skeleton
                variant="rectangular"
                width="100%"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  aspectRatio: '488 / 680',
                  borderRadius: '8px',
                }}
              />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

type DirectionFilter = 'all' | 'sent' | 'received';

export const TradeHistoryTab: React.FC<TradeHistoryTabProps> = ({ username }) => {
  const [page, setPage] = useState(1);
  const [previousPages, setPreviousPages] = useState<ProfileTrade[]>([]);
  const [tradeDirection, setTradeDirection] = useState<DirectionFilter>('all');
  const isPhone = useIsMobile();
  const { isOpen: isCartOpen } = useProfileTradeSidebar();
  const cardFilters = useCardFilterState();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const { data, isLoading, isFetching } = useGetProfileTradeHistoryQuery({
    username,
    page,
    limit: 20,
    tradeDirection,
    filters: cardFilters.requestArgs,
  });

  // Filters changed: restart the accumulated pages.
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
  }, [cardFilters.requestArgs]);

  // When loading more pages, accumulate previous results
  const handleLoadMore = () => {
    if (data?.trades) {
      setPreviousPages((prev) => [...prev, ...data.trades]);
    }
    setPage((p) => p + 1);
  };

  // Reset when username changes
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
    setTradeDirection('all');
  }, [username]);

  // Reset pagination when direction filter changes
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
  }, [tradeDirection]);

  if (isLoading) {
    return <TradeHistorySkeleton />;
  }

  // Combine previous pages with current page data
  const allTrades = [...previousPages, ...(data?.trades || [])];

  // Filtered to zero on a history that has trades (decision 12).
  const unfilteredTotal = data?.unfilteredTotal ?? 0;
  const showFilteredEmpty = cardFilters.hasActiveFilters && !isFetching && allTrades.length === 0 && unfilteredTotal > 0;

  const filterChrome = (
    <>
      {!isPhone && <CardFilterRail filters={cardFilters} headerTooltip={PROFILE_FILTER_TOOLTIP} cartOpen={isCartOpen} hideTradeSection />}
      {isPhone && (
        <CardFilterMobile
          filters={cardFilters}
          totalCount={data?.totalCount}
          sheetOpen={filterSheetOpen}
          onSheetOpenChange={setFilterSheetOpen}
          hideTradeSection
        />
      )}
    </>
  );

  // Truly no trade history — only show bare empty state when unfiltered
  if (!allTrades.length && tradeDirection === 'all' && !cardFilters.hasActiveFilters) {
    return (
      <>
        {filterChrome}
        <div className="trade-history-empty">
          <p>No trade history yet.</p>
        </div>
      </>
    );
  }

  const hasMore = data?.hasMore || false;

  // Calculate total cards from all trades
  const totalCards = allTrades.reduce((sum, trade) => sum + trade.items.length, 0);

  const filterEmptyMessage = tradeDirection === 'sent'
    ? 'No sent cards yet.'
    : 'No received cards yet.';

  return (
    <div className="trade-history-tab">
      <div className="trade-history-header">
        <span className="trade-history-count">{formatNumber(totalCards)} Cards</span>
        <div className="trade-history-filters">
          {(['all', 'sent', 'received'] as DirectionFilter[]).map((dir) => (
            <button
              key={dir}
              className={`trade-history-filter-pill ${dir}${tradeDirection === dir ? ' active' : ''}`}
              onClick={() => setTradeDirection(dir)}
            >
              {dir === 'all' ? 'All' : dir === 'sent' ? 'Sent' : 'Received'}
            </button>
          ))}
        </div>
      </div>

      <CardFilterTokenRow
        state={cardFilters.state}
        tokens={cardFilters.tokens}
        onChange={cardFilters.update}
        onClearToken={cardFilters.clearToken}
        onClearAll={cardFilters.clearAll}
        scrollable={isPhone}
      />

      {showFilteredEmpty ? (
        <FilteredEmptyState
          listNoun="this trader's trade history"
          unitNoun="trades"
          unfilteredTotal={unfilteredTotal}
          state={cardFilters.state}
          tokens={cardFilters.tokens}
          lastAddedToken={cardFilters.lastAddedToken}
          onClearToken={cardFilters.clearToken}
          onClearAll={cardFilters.clearAll}
        />
      ) : !allTrades.length ? (
        <div className="trade-history-empty">
          <p>{filterEmptyMessage}</p>
        </div>
      ) : (
        <>
          <div className="trade-history-content">
            {allTrades.map((trade) => (
              <TradeGroup key={trade.id} trade={trade} />
            ))}
          </div>

          {hasMore && (
            <div className="trade-history-load-more">
              <MagicButton
                label={isFetching ? 'Loading...' : 'Load More'}
                color="dark-grey"
                onPress={handleLoadMore}
                isLoading={isFetching}
                isDisabled={isFetching}
              />
            </div>
          )}
        </>
      )}
      {filterChrome}
    </div>
  );
};

export default TradeHistoryTab;
